/**
 * Permission helpers for the admin-side enhanced verification policy center
 * (Issue #270).
 *
 * Mirrors the backend `RequirePermission` decorators on
 * `apps/api/src/modules/enhanced-verification/controllers/enhanced-verification.controller.ts`:
 *   - `verification_policy:read`     gates policy / provider / fallback list
 *     and the audit-event read summary.
 *   - `verification_policy:write`    gates policy mutations (PATCH).
 *   - `verification_provider:write`  gates provider-readiness mutations.
 *   - `verification_audit:read`      gates the audit-event timeline.
 *
 * Red lines:
 *   - These helpers only decide UI visibility / interactivity. The backend
 *     remains the authoritative gate; a user who flips bits in DevTools
 *     still cannot mutate without the matching JWT permission.
 *   - The face provider switch is intentionally NOT covered by any helper --
 *     the first version offers no enable toggle in the UI at all.
 */

export const VERIFICATION_POLICY_READ_PERMISSION = 'verification_policy:read';
export const VERIFICATION_POLICY_WRITE_PERMISSION = 'verification_policy:write';
export const VERIFICATION_PROVIDER_WRITE_PERMISSION =
  'verification_provider:write';
export const VERIFICATION_AUDIT_READ_PERMISSION = 'verification_audit:read';

const SUPER_ADMIN = '*:*';

function hasAny(
  permissions: readonly string[],
  needles: readonly string[],
): boolean {
  if (permissions.includes(SUPER_ADMIN)) return true;
  for (const needle of needles) {
    if (permissions.includes(needle)) return true;
  }
  return false;
}

export function canReadEnhancedVerification(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [
    VERIFICATION_POLICY_READ_PERMISSION,
    VERIFICATION_POLICY_WRITE_PERMISSION,
    VERIFICATION_PROVIDER_WRITE_PERMISSION,
    VERIFICATION_AUDIT_READ_PERMISSION,
  ]);
}

export function canWriteVerificationPolicies(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [VERIFICATION_POLICY_WRITE_PERMISSION]);
}

export function canWriteVerificationProviderReadiness(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [VERIFICATION_PROVIDER_WRITE_PERMISSION]);
}

export function canReadVerificationAudit(
  permissions: readonly string[],
): boolean {
  return hasAny(permissions, [VERIFICATION_AUDIT_READ_PERMISSION]);
}
